import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom orange marker for platform location
const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map center updates
const MapController = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, 15);
    }
  }, [center, map]);
  
  return null;
};

// Component to handle draggable marker
const DraggableMarker = ({ position, onPositionChange }) => {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        onPositionChange(newPos.lat, newPos.lng);
      }
    },
  };

  if (!position || !position[0] || !position[1]) return null;

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={orangeIcon}
    />
  );
};

const LocationPickerMap = ({ 
  city, 
  province, 
  address, 
  civicNumber, 
  postalCode,
  latitude,
  longitude,
  onLocationChange 
}) => {
  const [mapCenter, setMapCenter] = useState([41.9028, 12.4964]); // Default: Rome, Italy
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');

  // Geocode address when location fields change
  useEffect(() => {
    const geocodeAddress = async () => {
      // Only geocode if we have at least city
      if (!city) {
        setMarkerPosition(null);
        return;
      }

      // Build address string
      const addressParts = [];
      if (address) addressParts.push(address);
      if (civicNumber) addressParts.push(civicNumber);
      if (city) addressParts.push(city);
      if (province) addressParts.push(province);
      if (postalCode) addressParts.push(postalCode);
      addressParts.push('Italia');

      const fullAddress = addressParts.join(', ');

      setIsGeocoding(true);
      setGeocodeError('');

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          setMapCenter([lat, lon]);
          setMarkerPosition([lat, lon]);
          onLocationChange(lat, lon);
          setGeocodeError('');
        } else {
          setGeocodeError('Indirizzo non trovato. Trascina il marker nella posizione corretta.');
          // Set a default position for Italy if geocoding fails
          if (!markerPosition) {
            const defaultLat = 41.9028;
            const defaultLon = 12.4964;
            setMapCenter([defaultLat, defaultLon]);
            setMarkerPosition([defaultLat, defaultLon]);
            onLocationChange(defaultLat, defaultLon);
          }
        }
      } catch (error) {
        console.error('Geocoding error:', error);
        setGeocodeError('Errore durante la ricerca. Trascina il marker nella posizione corretta.');
      }

      setIsGeocoding(false);
    };

    // Debounce geocoding
    const timeoutId = setTimeout(geocodeAddress, 1000);
    return () => clearTimeout(timeoutId);
  }, [city, province, address, civicNumber, postalCode]);

  // Use provided coordinates if available
  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter([latitude, longitude]);
      setMarkerPosition([latitude, longitude]);
    }
  }, [latitude, longitude]);

  const handleMarkerDrag = (lat, lng) => {
    setMarkerPosition([lat, lng]);
    onLocationChange(lat, lng);
    setGeocodeError('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700">
          Verifica posizione sulla mappa
        </label>
        {isGeocoding && (
          <span className="text-xs text-orange-600 animate-pulse">
            Ricerca posizione...
          </span>
        )}
      </div>
      
      <div className="relative h-48 w-full rounded-lg overflow-hidden border-2 border-slate-200">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={mapCenter} />
          <DraggableMarker 
            position={markerPosition} 
            onPositionChange={handleMarkerDrag}
          />
        </MapContainer>
      </div>

      {geocodeError && (
        <p className="text-xs text-amber-600">
          {geocodeError}
        </p>
      )}
      
      <p className="text-xs text-slate-500">
        Trascina il marker arancione per correggere la posizione se necessario.
      </p>
      
      {markerPosition && (
        <p className="text-xs text-slate-400">
          Coordinate: {markerPosition[0]?.toFixed(6)}, {markerPosition[1]?.toFixed(6)}
        </p>
      )}
    </div>
  );
};

export default LocationPickerMap;
